import {
  DeleteOutlineIcon,
  HideOutlineIcon,
  HistoryOutlineIcon,
  MoreVerticalOutlineIcon,
  PhotoSquareOutlineIcon,
  ShowOutlineIcon,
} from '@carboncopyco/icons';
import { FrameType } from '@carboncopyco/types';
import {
  Button,
  Dropdown as DropdownComponent,
  DropdownItem,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type RefObject, useRef } from 'react';

interface IDropdown {
  isFrameActive: boolean;
  frameHasStepWithJump: boolean;
  activeFramesLength: number;
  frameType: FrameType;
  isFrameConvertable: boolean;
  onVisibilityClick: () => void;
  onDeleteClick: () => void;
  onConvertToFrameClick: () => void;
}

const Dropdown = ({
  isFrameActive,
  frameHasStepWithJump,
  activeFramesLength,
  frameType,
  isFrameConvertable,
  onVisibilityClick,
  onDeleteClick,
  onConvertToFrameClick,
}: IDropdown) => {
  const VisibilityIcon = isFrameActive ? HideOutlineIcon : ShowOutlineIcon;
  const visibilityText = isFrameActive ? 'Hide' : 'Show';

  const flags = useFlags();

  const visibilityItemRef = useRef();
  const deleteItemRef = useRef();

  const onlyOneFrameIsActive = isFrameActive && activeFramesLength === 1;

  const frameIsActiveAndHasStepWithJump = isFrameActive && frameHasStepWithJump;
  const areItemsDisabled =
    onlyOneFrameIsActive || frameIsActiveAndHasStepWithJump;

  const renderTooltip = (label: string, anchorRef: RefObject<any>) => {
    return (
      <Tooltip
        placement="right"
        anchorRef={anchorRef}
        noArrow
      >
        <div className="px-2 py-2.5 w-max">
          <TextCaption label={label} />
        </div>
      </Tooltip>
    );
  };

  return (
    <DropdownComponent
      side="right"
      asChild
      triggerElement={
        <div data-testid="frame-context-menu-button">
          <Button
            icon={<MoreVerticalOutlineIcon />}
            dark={isFrameActive}
            highContrast={!isFrameActive}
          />
        </div>
      }
    >
      <div
        ref={visibilityItemRef}
        data-testid="frame-context-menu-visibility"
      >
        <DropdownItem
          label={visibilityText}
          onClick={onVisibilityClick}
          disabled={areItemsDisabled}
          iconLeft={<VisibilityIcon size={24} />}
        />
      </div>
      {flags.tourCaptureTakeScreenshots && isFrameConvertable && (
        <DropdownItem
          label={
            frameType === FrameType.recording
              ? 'Convert to screenshot '
              : 'Restore original frame'
          }
          onClick={onConvertToFrameClick}
          iconLeft={
            frameType === FrameType.recording ? (
              <PhotoSquareOutlineIcon size={24} />
            ) : (
              <HistoryOutlineIcon size={24} />
            )
          }
        />
      )}
      <div
        ref={deleteItemRef}
        data-testid="frame-context-menu-delete"
      >
        <DropdownItem
          label="Delete"
          onClick={onDeleteClick}
          disabled={areItemsDisabled}
          iconLeft={<DeleteOutlineIcon size={24} />}
        />
      </div>
      {onlyOneFrameIsActive &&
        renderTooltip(
          'At least one frame should be visible.',
          visibilityItemRef
        )}
      {onlyOneFrameIsActive &&
        renderTooltip('At least one frame should be visible.', deleteItemRef)}
      {frameIsActiveAndHasStepWithJump &&
        !onlyOneFrameIsActive &&
        renderTooltip(
          "Frame can't be hidden since it has steps with custom click action.",
          visibilityItemRef
        )}
      {frameIsActiveAndHasStepWithJump &&
        !onlyOneFrameIsActive &&
        renderTooltip(
          "Frame can't be deleted since it has steps with custom click action.",
          deleteItemRef
        )}
      <div />
    </DropdownComponent>
  );
};

export default Dropdown;
